import { useState, useMemo, memo } from "react";

import { cn, formatTimestamp } from "@/lib/utils";
import {
  User,
  Wrench,
  FileText,
  AlertCircle,
  Loader2,
  FileIcon,
  RefreshCw,
  Lightbulb,
  Bot,
  Check,
  Minus,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { MessageType, ToolStatus, TaskStatus } from "@optidev/shared-types";

import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  extractUIPayload,
  parseVisualEditorMessage,
  getElementSummary,
} from "@/lib/agent/payload-parser";
import { ClaudeResponseCard } from "./ClaudeResponseCard";
import { MarkdownRenderer } from "./MarkdownRenderer";

/** Must match sidecar MARKETPLACE_PREFIX and gateway SKILL_FOLDER_PREFIX */
const SKILL_PREFIX = "optidev-market-";
import { MentionBadge } from "./mention-badge";
import type { ThemeContent } from "@/types/theme.types";

export interface FileAttachment {
  id: string;
  refId: string;
  originalName: string;
  slug: string;
  assetUrl: string;
  thumbnailUrl?: string;
  downloadUrl?: string;
  fileSize: number;
  mimeType: string;
  intent: string;
}

// Active tool being executed (for real-time display)
export interface ActiveTool {
  entryId: string;
  toolUseId: string;
  toolName: string;
  toolInput?: Record<string, unknown>;
  status: ToolStatus;
  errorMessage?: string;
  resultSummary?: string; // Brief summary like "150 lines" or "234 chars"
  startedAt: Date;
  completedAt?: Date;
  textPosition?: number; // Character position in text when tool started (for inline display)
}

// Re-export for convenience
export { MessageType, ToolStatus, TaskStatus };

export interface Message {
  id: string;
  taskId?: string; // Task ID for lookup (persists after id becomes assistantMessageId)
  entryId?: string; // Database entry ID (set when message is persisted to backend)
  type: MessageType;
  content: string;
  metadata?: {
    toolName?: string;
    files?: string[];
    duration?: number;
  };
  timestamp: Date;
  files?: FileAttachment[]; // Linked files from database
  toolUsage?: {
    Read?: string[];
    Write?: string[];
    Edit?: string[];
  };
  activeTools?: ActiveTool[]; // Tools currently being executed (real-time tracking)
  status?: TaskStatus; // Task status
  isLoading?: boolean; // Placeholder loading state before Worker starts
  clickedActions?: string[]; // IDs of HITL action buttons that have been clicked
  isStale?: boolean; // True if this message's actions should be disabled (e.g., old approve_plan after user requested changes)
  parentEntryId?: string; // User message entry ID this assistant message responds to (for visual grouping)
  userMessageId?: string | null; // The triggering user message ID for turn-based grouping (all assistant responses in a turn share this)
  queuedReason?: string; // Reason task is queued (e.g., 'container_paused') - for UI indicator
}

interface AgentMessageProps {
  message: Message;
  onOpenFile?: (filePath: string) => void;
  onAction?: (actionId: string, messageId: string) => void;
  showHeader?: boolean; // Hide icon/timestamp for consecutive assistant messages
  streamingMessage?: string; // Rotating status message for loading state
  // Theme preview props (required for ThemePreviewCard in ClaudeResponseCard)
  workspaceId: string;
  previewIframe: HTMLIFrameElement | null;
  projectId: string;
  onEditTheme?: (theme: ThemeContent) => void;
  onThemeSaved?: (themeId: string) => void;
  onConnectionCreated?: (
    connectionId: string,
    connectorType: string,
    connectionName: string,
  ) => void;
  // Media library props
  devContainerId?: string;
  previewUrl?: string;
}

/**
 * Get a shortened display name for a tool
 */
function getToolDisplayName(
  toolName: string,
  toolInput?: Record<string, unknown>,
): string {
  // Remove common prefixes
  if (toolName.startsWith("mcp__")) {
    const parts = toolName.split("__");
    return parts[parts.length - 1];
  }
  // Show skill name for Skill tool calls
  if (toolName === "Skill" && toolInput) {
    const skillName = toolInput.skill || toolInput.name || toolInput.skill_name;
    if (typeof skillName === "string") {
      return `Skill: ${skillName}`;
    }
  }
  return toolName;
}

/**
 * Get the file path from tool input if available
 */
function getToolFilePath(toolInput?: Record<string, unknown>): string | null {
  if (!toolInput) return null;
  // Common parameter names for file paths
  const pathKeys = ["file_path", "path", "relative_path", "filePath"];
  for (const key of pathKeys) {
    if (toolInput[key] && typeof toolInput[key] === "string") {
      return toolInput[key] as string;
    }
  }
  return null;
}

/**
 * Shorten a file path to show only the last 2-3 segments
 */
function shortenPath(path: string): string {
  const segments = path.split("/").filter(Boolean);
  if (segments.length <= 2) {
    return segments.join("/");
  }
  return ".../" + segments.slice(-2).join("/");
}

/**
 * Categorize a tool by its name
 */
function getToolCategory(toolName: string): "read" | "edit" | "bash" | "other" {
  const name = toolName.toLowerCase();
  if (
    name.includes("read") ||
    name.includes("glob") ||
    name.includes("grep") ||
    name.includes("search") ||
    name.includes("find")
  ) {
    return "read";
  }
  if (name.includes("write") || name.includes("edit")) {
    return "edit";
  }
  if (
    name.includes("bash") ||
    name.includes("shell") ||
    name.includes("exec")
  ) {
    return "bash";
  }
  return "other";
}

/**
 * Get summary label for a tool group
 */
function getToolGroupSummary(tools: ActiveTool[]): string {
  if (tools.length === 0) return "";

  const categories = tools.map((t) => getToolCategory(t.toolName));
  const readCount = categories.filter((c) => c === "read").length;
  const editCount = categories.filter((c) => c === "edit").length;
  const bashCount = categories.filter((c) => c === "bash").length;
  const otherCount = categories.filter((c) => c === "other").length;

  const parts: string[] = [];
  if (readCount > 0)
    parts.push(`Read ${readCount} file${readCount > 1 ? "s" : ""}`);
  if (editCount > 0)
    parts.push(`Edited ${editCount} file${editCount > 1 ? "s" : ""}`);
  if (bashCount > 0)
    parts.push(`Ran ${bashCount} command${bashCount > 1 ? "s" : ""}`);
  if (otherCount > 0)
    parts.push(`Used ${otherCount} tool${otherCount > 1 ? "s" : ""}`);

  return parts.join(", ");
}

interface CollapsibleToolGroupProps {
  tools: ActiveTool[];
  isLast: boolean;
  isStreaming: boolean;
}

/**
 * Collapsible tool group - shows summary when collapsed, details when expanded
 */
const CollapsibleToolGroup = memo(function CollapsibleToolGroup({
  tools,
  isLast,
  isStreaming,
}: CollapsibleToolGroupProps) {
  // Auto-expand if last group and still streaming, otherwise collapsed
  const [isExpanded, setIsExpanded] = useState(isLast && isStreaming);

  // Extract marketplace/custom skill tools with their status (from Skill tool calls)
  const skillToolEntries = tools
    .filter((t) => t.toolName === "Skill")
    .map((t) => {
      const raw =
        t.toolInput?.skill ??
        t.toolInput?.name ??
        t.toolInput?.skill_name;
      if (typeof raw !== "string" || !raw.startsWith(SKILL_PREFIX)) return null;
      return { slug: raw.replace(SKILL_PREFIX, ""), status: t.status };
    })
    .filter((s): s is { slug: string; status: ToolStatus } => s !== null);

  // Also detect Read tool calls that read SKILL.md files (base skills like frontend, themes, etc.)
  const SKILLS_PATH_PATTERN = /\.claude\/skills\/([^/]+)\/SKILL\.md$/;
  const readSkillEntries = tools
    .filter((t) => t.toolName === "Read")
    .map((t) => {
      const filePath = t.toolInput?.file_path ?? t.toolInput?.path;
      if (typeof filePath !== "string") return null;
      const match = filePath.match(SKILLS_PATH_PATTERN);
      if (!match) return null;
      const dirName = match[1];
      const isBase = !dirName.startsWith("optidev-market-");
      return { slug: dirName, status: t.status, isBaseSkill: isBase };
    })
    .filter((s): s is { slug: string; status: ToolStatus; isBaseSkill: boolean } => s !== null);

  // Combine both Skill tool calls and Read-based skill loads
  const allSkillEntries = [
    ...skillToolEntries.map((s) => ({ ...s, isBaseSkill: false })),
    ...readSkillEntries,
  ];

  // Deduplicate by slug, keep the latest status
  const skillMap = new Map<string, { status: ToolStatus; isBaseSkill: boolean }>();
  allSkillEntries.forEach((s) => skillMap.set(s.slug, { status: s.status, isBaseSkill: s.isBaseSkill }));
  const activeMarketplaceSkills = [...skillMap.entries()].map(([slug, { status, isBaseSkill }]) => ({ slug, status, isBaseSkill }));

  // Filter out skill tools for summary/status (they get their own cards)
  const nonSkillTools = tools.filter((t) => {
    if (t.toolName === "Skill") {
      const raw = t.toolInput?.skill ?? t.toolInput?.name ?? t.toolInput?.skill_name;
      return !(typeof raw === "string" && raw.startsWith(SKILL_PREFIX));
    }
    if (t.toolName === "Read") {
      const filePath = t.toolInput?.file_path ?? t.toolInput?.path;
      if (typeof filePath === "string" && SKILLS_PATH_PATTERN.test(filePath)) return false;
    }
    return true;
  });

  const allCompleted = tools.every((t) => t.status === ToolStatus.COMPLETED);
  const hasError = nonSkillTools.some((t) => t.status === ToolStatus.ERROR);
  const hasRunning = tools.some((t) => t.status === ToolStatus.RUNNING);
  const errorCount = nonSkillTools.filter((t) => t.status === ToolStatus.ERROR).length;
  const successCount = nonSkillTools.filter(
    (t) => t.status === ToolStatus.COMPLETED,
  ).length;
  const isPartialSuccess = hasError && successCount > 0;
  const summary = getToolGroupSummary(nonSkillTools);
  const hasNonSkillTools = nonSkillTools.length > 0;

  if (tools.length === 0) return null;

  return (
    <div className="mt-2">
      {/* Skill loaded cards — always visible (like Claude Code CLI) */}
      {activeMarketplaceSkills.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {activeMarketplaceSkills.map(({ slug, status, isBaseSkill }) => {
            const isCustom = slug.startsWith("custom-");
            const displaySlug = slug.replace(/^custom-/, "");
            const isLoading = status === ToolStatus.RUNNING;
            const isFailed = status === ToolStatus.ERROR;
            return (
              <div key={slug} className="text-xs leading-relaxed">
                <div className="flex items-center gap-1.5">
                  <span className={cn(
                    "w-1.5 h-1.5 rounded-full flex-shrink-0",
                    isLoading && "bg-blue-400 animate-pulse",
                    isFailed && "bg-amber-400",
                    !isLoading && !isFailed && (isBaseSkill ? "bg-blue-400" : isCustom ? "bg-violet-400" : "bg-emerald-400"),
                  )} />
                  <span className="font-mono font-semibold text-muted-foreground">
                    Skill(/{displaySlug})
                  </span>
                </div>
                <div className={cn(
                  "flex items-center gap-1 ml-3 pl-0.5",
                  isFailed ? "text-amber-500/70" : "text-muted-foreground/50",
                )}>
                  <span className="select-none text-muted-foreground/30">{"\u2514"}</span>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                      <span>Loading skill…</span>
                    </>
                  ) : isFailed ? (
                    <span>Failed to load skill</span>
                  ) : (
                    <span>Successfully loaded skill</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary row - only show if there are non-skill tools */}
      {hasNonSkillTools && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "group inline-flex items-center gap-1 text-xs transition-colors text-muted-foreground hover:text-foreground",
            activeMarketplaceSkills.length > 0 && "mt-1",
            hasError && !isPartialSuccess && "text-amber-600 dark:text-amber-500",
            isPartialSuccess && "text-amber-600 dark:text-amber-500",
            hasRunning && "text-blue-500",
          )}
        >
          {hasRunning ? (
            <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
          ) : hasError ? (
            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0" />
          ) : allCompleted ? (
            <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
          ) : null}

          <span className="hover:underline">
            {summary}
            {hasError && !hasRunning && (
              <span className="ml-1 text-amber-600 dark:text-amber-500 group-hover:text-inherit">
                ({errorCount} failed)
              </span>
            )}
          </span>

          {isExpanded ? (
            <ChevronDown className="w-3 h-3 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3 h-3 flex-shrink-0" />
          )}
        </button>
      )}

      {/* Expanded details */}
      {isExpanded && hasNonSkillTools && (
        <div className="mt-1 ml-1 space-y-1 border-l-2 border-border/30 pl-2">
          {nonSkillTools.map((tool) => {
            const displayName = getToolDisplayName(
              tool.toolName,
              tool.toolInput,
            );
            const filePath = getToolFilePath(tool.toolInput);

            return (
              <div
                key={tool.toolUseId}
                className={cn(
                  "flex items-center gap-2 px-2 py-1 rounded text-xs",
                  tool.status === ToolStatus.ERROR &&
                    "text-red-600 dark:text-red-400",
                )}
              >
                {/* Status indicator */}
                {tool.status === ToolStatus.RUNNING && (
                  <Loader2 className="w-3 h-3 animate-spin text-blue-500 flex-shrink-0" />
                )}
                {tool.status === ToolStatus.COMPLETED && (
                  <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
                )}
                {tool.status === ToolStatus.ERROR && (
                  <Minus className="w-3 h-3 text-red-500 flex-shrink-0" />
                )}

                {/* Tool name */}
                <span className="font-medium text-foreground/80">
                  {displayName}
                </span>

                {/* File path if available */}
                {filePath && (
                  <span
                    className="text-muted-foreground truncate max-w-[180px]"
                    title={filePath}
                  >
                    {shortenPath(filePath)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

/**
 * Text-tool segment for inline rendering
 */
interface TextToolSegment {
  text: string;
  tools: ActiveTool[];
}

/**
 * Extract @project:Name references from message content
 * Returns the cleaned content and an array of project names
 */
interface MentionRef {
  type: "project" | "file" | "theme" | "skill";
  name: string;
  id?: string;
}

/**
 * Extract @project:Name[id], @file:path, and @theme:name from message content.
 */
function extractMentionRefs(content: string): {
  cleanContent: string;
  mentions: MentionRef[];
} {
  const mentions: MentionRef[] = [];

  // @project:Name[id] or @project:Name
  const projectPattern = /@project:([^\s,\[]+)(?:\[([^\]]+)\])?/g;
  let match;
  while ((match = projectPattern.exec(content)) !== null) {
    mentions.push({ type: "project", name: match[1], id: match[2] });
  }

  // @file:path/to/file.tsx
  const filePattern = /@file:([^\s,]+)/g;
  while ((match = filePattern.exec(content)) !== null) {
    mentions.push({ type: "file", name: match[1] });
  }

  // @theme:ThemeName
  const themePattern = /@theme:([^\s,]+)/g;
  while ((match = themePattern.exec(content)) !== null) {
    mentions.push({ type: "theme", name: match[1] });
  }

  // @skill:slug — format slug as readable display name
  const skillPattern = /@skill:([^\s,]+)/g;
  while ((match = skillPattern.exec(content)) !== null) {
    const rawSlug = match[1];
    const displayName = rawSlug
      .replace(/^custom-/, "")
      .split("-")
      .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    mentions.push({ type: "skill", name: displayName, id: rawSlug });
  }

  const cleanContent = content
    .replace(projectPattern, "")
    .replace(filePattern, "")
    .replace(themePattern, "")
    .replace(skillPattern, "")
    .replace(/^\s+/, "")
    .trim();

  return { cleanContent, mentions };
}

/**
 * Parse message content and tools into segments for inline display
 * Groups tools by their textPosition and interleaves with text chunks
 */
function parseTextToolSegments(
  content: string,
  tools: ActiveTool[],
): TextToolSegment[] {
  if (!tools || tools.length === 0) {
    return [{ text: content, tools: [] }];
  }

  // Get unique text positions, sorted
  const positions = [
    ...new Set(tools.map((t) => t.textPosition ?? content.length)),
  ].sort((a, b) => a - b);

  // Group tools by position
  const toolsByPosition = new Map<number, ActiveTool[]>();
  tools.forEach((tool) => {
    const pos = tool.textPosition ?? content.length;
    if (!toolsByPosition.has(pos)) {
      toolsByPosition.set(pos, []);
    }
    toolsByPosition.get(pos)!.push(tool);
  });

  // Create segments
  const segments: TextToolSegment[] = [];
  let lastPos = 0;

  positions.forEach((pos) => {
    // Text before this tool group (trim to remove \n\n separators)
    const text = content.slice(lastPos, pos).trim();
    const toolsAtPos = toolsByPosition.get(pos) || [];

    // Only add segment if there's text or tools
    if (text || toolsAtPos.length > 0) {
      segments.push({ text, tools: toolsAtPos });
    }
    lastPos = pos;
  });

  // Remaining text after last tool position
  if (lastPos < content.length) {
    const remainingText = content.slice(lastPos).trim();
    if (remainingText) {
      segments.push({ text: remainingText, tools: [] });
    }
  }

  return segments;
}

export const AgentMessage = memo(function AgentMessage({
  message,
  onOpenFile,
  onAction,
  showHeader = true,
  streamingMessage,
  workspaceId,
  previewIframe,
  projectId,
  onEditTheme,
  onThemeSaved,
  onConnectionCreated,
  devContainerId,
  previewUrl,
}: AgentMessageProps) {
  const isUser = message.type === MessageType.USER;
  const isError = message.type === MessageType.ERROR;
  const isTool = message.type === MessageType.TOOL_USE;
  const isSystem = message.type === MessageType.SYSTEM;
  const { getIdToken } = useAuth();

  // Track which images have failed and their refreshed URLs
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [refreshedUrls, setRefreshedUrls] = useState<Record<string, string>>(
    {},
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Determine if this message is currently streaming (status is running and not completed)
  const isStreaming = message.status === TaskStatus.RUNNING;

  // Extract @project/@file/@theme mentions from user messages
  const mentionRefs = useMemo(() => {
    if (!isUser || !message.content) return null;
    const result = extractMentionRefs(message.content);
    return result.mentions.length > 0 ? result : null;
  }, [isUser, message.content]);

  // Parse visual editor message for clean display
  const visualEditorData = useMemo(() => {
    if (!isUser || !message.content) return null;
    const content = mentionRefs ? mentionRefs.cleanContent : message.content;
    return parseVisualEditorMessage(content);
  }, [isUser, message.content, mentionRefs]);

  // Parse content + tools into segments for inline rendering
  // parseTextToolSegments handles undefined textPosition by placing at end (t.textPosition ?? content.length)
  const segments = useMemo(() => {
    if (
      message.type !== MessageType.ASSISTANT ||
      !message.activeTools?.length
    ) {
      return null;
    }
    return parseTextToolSegments(message.content || "", message.activeTools);
  }, [message.content, message.activeTools, message.type]);

  // Refresh all images in this message
  const handleRefreshImages = async () => {
    if (!message.files || isRefreshing) return;

    setIsRefreshing(true);

    try {
      const token = await getIdToken();

      // Fetch fresh presigned URLs for all files
      const refreshPromises = message.files.map(async (file) => {
        try {
          const response = await axios.get(
            `/api/files/${file.refId}/download`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          return { refId: file.refId, url: response.data.url };
        } catch (error) {
          console.error(
            `[AgentMessage] Failed to refresh ${file.refId}:`,
            error,
          );
          return null;
        }
      });

      const results = await Promise.all(refreshPromises);

      // Update all URLs and clear errors
      const newUrls: Record<string, string> = {};
      results.forEach((result) => {
        if (result) {
          newUrls[result.refId] = result.url;
        }
      });

      setRefreshedUrls((prev) => ({ ...prev, ...newUrls }));
      setImageErrors({}); // Clear all errors
      console.log("[AgentMessage] Refreshed all images");
    } catch (error) {
      console.error("[AgentMessage] Failed to refresh images:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleImageError = (refId: string) => {
    setImageErrors((prev) => ({ ...prev, [refId]: true }));
  };

  const getIcon = () => {
    switch (message.type) {
      case MessageType.USER:
        return <User className="w-4 h-4" />;
      case MessageType.ASSISTANT:
        return <Bot className="w-4 h-4" />;
      case MessageType.TOOL_USE:
        return <Wrench className="w-4 h-4" />;
      case MessageType.ERROR:
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  // Parse UI payload for assistant messages - check during streaming too
  const parsedResponse =
    message.type === MessageType.ASSISTANT
      ? extractUIPayload(message.content)
      : null;
  const hasUIPayload = parsedResponse?.payload !== null;

  // Check if payload is complete (has closing tag) - important for streaming
  const isPayloadComplete = message.content.includes("</ui_payload>");

  // Check if message has started a ui_payload but not finished
  const isStreamingPayload =
    message.type === MessageType.ASSISTANT &&
    message.content.includes("<ui_payload>") &&
    !isPayloadComplete;

  // Hide streaming content to prevent showing Claude's thinking text
  if (isStreamingPayload) {
    return null; // Return null - the "Thinking..." indicator in AgentChat will show instead
  }

  // If assistant message has COMPLETE UI payload, render the clean card
  if (
    message.type === MessageType.ASSISTANT &&
    hasUIPayload &&
    isPayloadComplete &&
    parsedResponse?.payload
  ) {
    return (
      <div className="p-3">
        {/* Only show bot icon header if showHeader is true (respects turn-based grouping) */}
        {showHeader && (
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-2 px-1.5 py-1.5 rounded-lg bg-gradient-to-br from-muted/40 to-muted/60 border border-border/50">
              <Bot className="w-3.5 h-3.5 text-foreground/70" />
            </div>
          </div>
        )}

        <ClaudeResponseCard
          payload={parsedResponse.payload}
          markdown={parsedResponse.markdown}
          messageId={message.id}
          onOpenFile={onOpenFile}
          onAction={onAction}
          clickedActions={message.clickedActions}
          isStale={message.isStale}
          onViewCode={() => console.log("View code")}
          onRestore={() => console.log("Restore")}
          workspaceId={workspaceId}
          previewIframe={previewIframe}
          projectId={projectId}
          onEditTheme={onEditTheme}
          onThemeSaved={onThemeSaved}
          onConnectionCreated={onConnectionCreated}
          devContainerId={devContainerId}
          previewUrl={previewUrl}
        />
      </div>
    );
  }

  // Reduce padding for consecutive assistant messages (no header shown)
  const isConsecutiveAssistant = !isUser && !showHeader;

  return (
    <div
      className={cn(
        isConsecutiveAssistant ? "px-2.5 pt-0 pb-2.5" : "p-2.5",
        isUser && "flex flex-col items-end",
      )}
    >
      {/* Timestamp before user message */}
      {isUser && (
        <span className="text-xs text-muted-foreground mb-1">
          {formatTimestamp(message.timestamp)}
        </span>
      )}

      {/* Header - only show for first assistant message in consecutive group */}
      {!isUser && showHeader && (
        <div className="mb-2 w-full">
          <div className="flex items-center gap-2 mb-2">
            {!isError && !isTool ? (
              <div className="flex items-center gap-2 px-1.5 py-1.5 rounded-lg bg-gradient-to-br from-muted/40 to-muted/60 border border-border/50">
                <Bot className="w-3.5 h-3.5 text-foreground/70" />
              </div>
            ) : (
              <div
                className={cn(
                  "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                  isError
                    ? "bg-destructive text-destructive-foreground"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {getIcon()}
              </div>
            )}
          </div>

          {/* Status pill - show when task is queued or running */}
          {message.status && message.status !== TaskStatus.COMPLETED && (
            <div className="pl-2.5">
              <Badge
                variant="secondary"
                className={cn(
                  "text-[10px] px-2 py-0.5 h-5 rounded-sm inline-flex items-center gap-1.5 cursor-default",
                  message.status === TaskStatus.RUNNING &&
                    "bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-950",
                  message.status === TaskStatus.QUEUED &&
                    "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-950",
                )}
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                {message.status === TaskStatus.QUEUED
                  ? message.queuedReason === "container_paused"
                    ? "Waiting for environment"
                    : "Queued"
                  : "Running"}{" "}
                - safe to navigate away
              </Badge>
            </div>
          )}
        </div>
      )}

      {/* Loading state for placeholder message */}
      {message.isLoading && (
        <div className="pl-2.5 py-2.5">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lightbulb className="w-3.5 h-3.5" />
            <span className="flex items-baseline">
              {/* Strip trailing "..." from streamingMessage since we add animated dots */}
              {(streamingMessage || "Thinking...").replace(/\.{3}$/, "")}
              <span className="inline-flex ml-0.5">
                <span
                  className="opacity-0"
                  style={{
                    animation: "dotSequence 1.6s ease-in-out infinite",
                    animationDelay: "0s",
                  }}
                >
                  .
                </span>
                <span
                  className="opacity-0"
                  style={{
                    animation: "dotSequence 1.6s ease-in-out infinite",
                    animationDelay: "0.3s",
                  }}
                >
                  .
                </span>
                <span
                  className="opacity-0"
                  style={{
                    animation: "dotSequence 1.6s ease-in-out infinite",
                    animationDelay: "0.6s",
                  }}
                >
                  .
                </span>
              </span>
            </span>
          </div>
          <style>{`
            @keyframes dotSequence {
              0% { opacity: 0; }
              25% { opacity: 1; }
              75% { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
        </div>
      )}

      {/* Message content - hide if loading (except system messages which always show) */}
      {(!message.isLoading || isSystem) && (
        <div className={cn(isUser && "max-w-[85%] min-w-0")}>
          <div
            className={cn(
              "py-2.5 rounded-xl break-words whitespace-pre-wrap",
              isUser &&
                "bg-gradient-to-br from-primary/85 to-primary text-primary-foreground px-3.5",
              isError &&
                "bg-destructive/10 border border-destructive/20 px-3.5",
              isSystem &&
                "bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 px-3.5 text-center",
              !isUser && !isError && !isSystem && "pl-2.5",
            )}
            style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
          >
            {isTool && message.metadata?.toolName && (
              <Badge variant="outline" className="text-xs mb-2">
                {message.metadata.toolName}
              </Badge>
            )}

            {message.metadata?.files && message.metadata.files.length > 0 && (
              <Badge variant="secondary" className="text-xs mb-2">
                {message.metadata.files.length} file
                {message.metadata.files.length > 1 ? "s" : ""} modified
              </Badge>
            )}

            {/* Render content with inline tools when segments available */}
            {segments ? (
              // Segment-based rendering: text + tools interleaved
              <div className="space-y-0">
                {segments.map((segment, idx) =>
                  // Skip empty segments (no text and no tools)
                  segment.text || segment.tools.length > 0 ? (
                    <div key={idx}>
                      {/* Text chunk */}
                      {segment.text && (
                        <MarkdownRenderer
                          content={segment.text}
                          isStreaming={
                            isStreaming && idx === segments.length - 1
                          }
                          className={cn(
                            "text-sm",
                            isUser &&
                              "prose-invert [&_*]:text-primary-foreground",
                            isError && "[&_*]:text-destructive",
                          )}
                        />
                      )}
                      {/* Tools after this text chunk - only add bottom margin if more segments follow */}
                      {segment.tools.length > 0 && (
                        <div
                          className={cn(
                            "tool-block",
                            idx < segments.length - 1 && "mb-3",
                          )}
                        >
                          <CollapsibleToolGroup
                            tools={segment.tools}
                            isLast={idx === segments.length - 1}
                            isStreaming={isStreaming}
                          />
                        </div>
                      )}
                    </div>
                  ) : null,
                )}
              </div>
            ) : (
              // Fallback: simple content rendering (no tools or non-assistant)
              message.content && (
                <>
                  {/* Visual Editor Command - Clean Display */}
                  {visualEditorData ? (
                    <div className="space-y-2.5">
                      {/* Command text */}
                      <MarkdownRenderer
                        content={visualEditorData.command}
                        isStreaming={isStreaming}
                        className={cn(
                          "text-sm",
                          isUser &&
                            "prose-invert [&_*]:text-primary-foreground",
                        )}
                      />

                      {/* Element info badge */}
                      {visualEditorData.elementContext && (
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary-foreground/10 border border-primary-foreground/20">
                          <span className="text-[11px] font-medium opacity-90">
                            {getElementSummary(visualEditorData.elementContext)}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Regular message (with mention badges if present)
                    <>
                      {mentionRefs && mentionRefs.mentions.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-1.5">
                          {mentionRefs.mentions.map((ref, idx) => (
                            <MentionBadge
                              key={`${ref.type}-${ref.id || ref.name}-${idx}`}
                              type={ref.type}
                              name={ref.name}
                              id={ref.id}
                              variant="onPrimary"
                            />
                          ))}
                        </div>
                      )}
                      <MarkdownRenderer
                        content={(() => {
                          const raw = mentionRefs ? mentionRefs.cleanContent : message.content;
                          // Truncate slides plan — show only the clean prompt, not the full plan blob
                          const planIdx = raw.indexOf('\n## Pre-extracted Plan');
                          if (planIdx > 0) return raw.substring(0, planIdx);
                          return raw;
                        })()}
                        isStreaming={isStreaming}
                        className={cn(
                          "text-sm",
                          isUser &&
                            "prose-invert [&_*]:text-primary-foreground",
                          isError && "[&_*]:text-destructive",
                        )}
                      />
                    </>
                  )}
                </>
              )
            )}

            {/* Tool usage pills are now rendered inline with the last tool segment above */}
            {/* Removed fallback to prevent duplication when activeTools is missing */}

            {/* File attachments from database */}
            {message.files && message.files.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {message.files.map((file) => {
                  const isImage = file.mimeType.startsWith("image/");
                  const isPDF = file.mimeType.includes("pdf");
                  const isVideo = file.mimeType.startsWith("video/");

                  return (
                    <div
                      key={file.id}
                      className="relative group bg-muted rounded-lg border border-border overflow-hidden hover:border-primary/50 transition-colors"
                      style={{ width: "80px", height: "80px" }}
                    >
                      {/* Image/thumbnail preview or file icon */}
                      {isImage ? (
                        <div className="relative w-full h-full">
                          <img
                            src={
                              refreshedUrls[file.refId] ||
                              file.thumbnailUrl ||
                              file.downloadUrl
                            }
                            alt={file.originalName}
                            className="w-full h-full object-cover"
                            onError={() => handleImageError(file.refId)}
                          />
                          {/* Show refresh icon on broken images */}
                          {imageErrors[file.refId] && (
                            <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                              <button
                                onClick={handleRefreshImages}
                                disabled={isRefreshing}
                                className="p-2 rounded-full bg-muted hover:bg-accent transition-colors disabled:opacity-50"
                                title="Refresh image"
                              >
                                {isRefreshing ? (
                                  <Loader2 className="w-5 h-5 text-foreground animate-spin" />
                                ) : (
                                  <RefreshCw className="w-5 h-5 text-foreground" />
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted">
                          {isPDF ? (
                            <FileText className="w-8 h-8 text-red-400" />
                          ) : isVideo ? (
                            <FileIcon className="w-8 h-8 text-purple-400" />
                          ) : (
                            <FileIcon className="w-8 h-8 text-blue-400" />
                          )}
                        </div>
                      )}

                      {/* Hover overlay with filename */}
                      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-1">
                        <span className="text-[10px] text-white text-center truncate w-full px-1">
                          {file.originalName}
                        </span>
                      </div>

                      {/* Intent badge */}
                      <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Badge
                          variant="secondary"
                          className="text-[8px] px-1 py-0 h-4"
                        >
                          {file.intent === "reference" ? "Ref" : "App"}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tool metadata files */}
            {message.metadata?.files && message.metadata.files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {message.metadata.files.map((file, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs text-muted-foreground"
                  >
                    <FileText className="w-3 h-3" />
                    {file}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
